import {toast, ToastCard} from '@sollapay/ui/components';
import {useDirection} from '@sollapay/ui/providers';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {fetchLatestEntryScriptSrc, getCurrentEntryScriptSrc} from './new-version.utils';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const TOAST_ID = 'new-version';

export interface NewVersionNotifierProps {
  ns?: string;
}

export function NewVersionNotifier({ns = 'common'}: NewVersionNotifierProps) {
  const {t} = useTranslation(ns);
  const position: 'bottom-left' | 'bottom-right' =
    useDirection() === 'rtl' ? 'bottom-left' : 'bottom-right';
  const [isToastOpen, setIsToastOpen] = useState(false);

  useEffect(() => {
    const currentSrc = getCurrentEntryScriptSrc();
    if (!currentSrc) return;

    let notifiedSrc: string | undefined;

    const check = async () => {
      if (document.hidden) return;
      const latestSrc = await fetchLatestEntryScriptSrc();
      if (!latestSrc || latestSrc === currentSrc || latestSrc === notifiedSrc) return;

      notifiedSrc = latestSrc;
      setIsToastOpen(true);
    };

    const intervalId = setInterval(() => void check(), CHECK_INTERVAL_MS);
    const onVisibilityChange = () => void check();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Re-issuing a toast with the same id updates it in place, so a language or direction change
  // re-translates and moves the open toast; a dismissed toast stays closed.
  useEffect(() => {
    if (!isToastOpen) return;

    toast.custom(
      id => (
        <ToastCard
          iconName="sollapay:sparkles"
          title={t('newVersionToast.title')}
          description={t('newVersionToast.description')}
          action={{label: t('newVersionToast.action'), onClick: () => window.location.reload()}}
          onClose={() => toast.dismiss(id)}
          closeLabel={t('newVersionToast.close')}
        />
      ),
      {id: TOAST_ID, duration: Infinity, position, onDismiss: () => setIsToastOpen(false)}
    );
  }, [isToastOpen, t, position]);

  return null;
}
